import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getMe, hydrateIdentity, resetIdentityForSwitch } from '@/lib/auth'
import { usersApi } from '@/modules/iam/api/users-api'
import {
  clearImpersonationTarget,
  setImpersonationTarget,
} from '@/lib/impersonation'
import { focusMain } from '@/lib/use-route-focus'
import { useSelectedEntityId } from '@/lib/use-selected-entity'
import { useAnnounce } from '@/shared/providers/announcer-provider'

export function useStartImpersonation() {
  const queryClient = useQueryClient()
  const entityId = useSelectedEntityId()
  return useMutation({
    mutationFn: async (targetUserId: string) => {
      await usersApi.startImpersonation(targetUserId)
      setImpersonationTarget(targetUserId)
      resetIdentityForSwitch()
      queryClient.clear()
      await hydrateIdentity(entityId ?? undefined)
    },
  })
}

export function useStopImpersonation() {
  const queryClient = useQueryClient()
  const entityId = useSelectedEntityId()
  const announce = useAnnounce()
  return useMutation({
    mutationFn: async () => {
      const actorName = getMe()?.impersonation?.actorName
      try {
        await usersApi.stopImpersonation()
      } finally {
        clearImpersonationTarget()
        resetIdentityForSwitch()
        queryClient.clear()
        await hydrateIdentity(entityId ?? undefined)
      }
      return actorName
    },
    onSettled: (actorName) => {
      focusMain()
      announce(
        actorName
          ? `Exited impersonation, viewing as ${actorName}.`
          : 'Exited impersonation.',
      )
    },
  })
}
